import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'

import P from '../../../components/typography/P'

import ProfessionalHeadlinesDnDInput from '../../../components/ProfessionalHeadlinesDnDInput'
import TechnicalSkillInput from '../../../components/TechnicalSkillsInput'
import { MultiSelect } from '../../../components/Select'

import { getFromLocalStorage, parseJSON } from '../../../utils/util'

import {
  setProfessionalHeadlines,
  setTechnicalSkills,
  setSoftSkills,
} from '../../../redux/feature/applicantSlice'

import apiClient from '../../../helpers/apiClient'

const Skills = forwardRef((props, ref) => {
  const dispatch = useDispatch()

  const { professionalHeadlines, technicalSkills, softSkills } = useSelector(
    (state) => state.applicant
  )

  const validationSchema = useMemo(() => {
    return Joi.object({
      professionalHeadlines: Joi.array()
        .items(
          Joi.object({
            value: Joi.alternatives()
              .try(Joi.string(), Joi.number())
              .required(),
            label: Joi.string().required(),
            status: Joi.string().required(),
          })
        )
        .label('Professional Headlines')
        .min(1)
        .required()
        .messages({
          'array.base': 'Professional Headlines must be a valid list.',
          'array.min': 'At least one Professional Headline is required.',
          'any.required': 'Professional Headlines is required.',
        }),

      technicalSkills: Joi.array()
        .items(
          Joi.object({
            id: Joi.alternatives().try(Joi.string(), Joi.number()).required(),
            skill: Joi.string().required(),
            experience: Joi.alternatives()
              .try(Joi.string(), Joi.number())
              .required(),
          })
        )
        .label('Technical Skills')
        .min(1)
        .required()
        .messages({
          'array.base': 'Technical Skills must be a valid list.',
          'array.min': 'At least one Technical Skill is required.',
          'any.required': 'Technical Skills is required.',
        }),

      softSkills: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .label('Soft Skills')
        .min(1)
        .required()
        .messages({
          'array.base': 'Soft Skills must be a valid list.',
          'array.min': 'At least one Soft Skill is required.',
          'any.required': 'Soft Skills is required.',
        }),
    })
  }, [])

  const formRef = useRef(null)

  const [fieldErrors, setFieldErrors] = useState({})

  const [softSkillList, setSoftSkillList] = useState([])

  const applicantCVData = useMemo(
    () => getFromLocalStorage('applicant-cv-data'),
    []
  )

  const professionalHeadlineIds = useMemo(
    () => professionalHeadlines?.length > 0 ? professionalHeadlines?.map((e) => e.value) : [],
    [professionalHeadlines?.length]
  ) || []

  /**
   * @returns {boolean} Whether the form data is valid.
   */
  const handleNext = () => {
    const payload = {
      professionalHeadlines,
      technicalSkills,
      softSkills,
    }

    // Validate form data against schema, collecting all errors
    const result = validationSchema.validate(payload, { abortEarly: false })

    // If there are validation errors, set error messages for fields and notify the user
    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      toast.error('Please fix the validation errors to continue.')

      return
    }

    const meta = parseJSON(applicantCVData?.meta, {})

    const updatedMeta = {
      ...meta,
      fullSkills: [technicalSkills, softSkills],
    }

    const updatedApplicantCVData = {
      ...applicantCVData,
      professional_headline: JSON.stringify(professionalHeadlines),
      meta: JSON.stringify(updatedMeta),
    }

    // save to local storage...
    localStorage.setItem(
      'applicant-cv-data',
      JSON.stringify(updatedApplicantCVData)
    )

    return true
  }

  const handleProfessionalHeadlinesChanged = (headlines) => {
    const movedHeadlines = headlines
      .filter((e) => e.status === 'moved')
      .map((e) => ({
        value: e.id,
        label: e.item,
        status: e.status,
      }))

    dispatch(setProfessionalHeadlines(movedHeadlines))
  }

  const handleTechnicalSkillsChanged = (existingSkills) => {
    dispatch(
      setTechnicalSkills(
        existingSkills?.map((e) => ({
          skill: e.label,
          id: e.value,
          experience: e?.experience || 1,
        }))
      )
    )
  }

  const handleSoftSkillsChanged = (existingSkills) =>
    dispatch(setSoftSkills(existingSkills))

  useEffect(() => {
    apiClient
      .get(`/skills`)
      .then((res) => {
        const result = res?.data?.data?.softSkills
        const newSoftSkills = result?.map((e) => ({ value: e, label: e }))

        setSoftSkillList(newSoftSkills)
      })
      .catch((err) => console.log(err))

    if (applicantCVData) {
      const savedProfessionalHeadlines = parseJSON(
        applicantCVData?.professional_headline,
        []
      )

      dispatch(setProfessionalHeadlines(savedProfessionalHeadlines || []))

      const meta = parseJSON(applicantCVData?.meta, [])
      const fullSkills = meta?.fullSkills || []

      dispatch(setTechnicalSkills(fullSkills[0]))
      dispatch(setSoftSkills(fullSkills[1]))
    }
  }, [])

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledJobExperiences>
      <P size="1.5rem" weight="500" mb="0.25rem">
        Skills
      </P>
      <P color="#6F6F6F" mb="1rem">
        Highlight your core skills, proficiencies which are relevant to the
        positions you are seeking.
      </P>
      <Container>
        <FormContainer ref={formRef}>
          <FormGroup>
            <ProfessionalHeadlinesDnDInput
              currentHeadlines={professionalHeadlines || []}
              onHeadlinesChange={handleProfessionalHeadlinesChanged}
              errorMessage={
                'professionalHeadlines' in fieldErrors &&
                fieldErrors?.professionalHeadlines
              }
            />
          </FormGroup>
          <FormGroup>
            <TechnicalSkillInput
              skills={technicalSkills}
              onSelectTechnicalSkills={handleTechnicalSkillsChanged}
              professionalHeadlineIds={professionalHeadlineIds}
              fieldError={fieldErrors?.technicalSkills}
            />
          </FormGroup>
          <FormGroup>
            <MultiSelect
              title="Soft Skills"
              options={softSkillList}
              selected={softSkills}
              placeholder="Select Soft Skills"
              onChange={handleSoftSkillsChanged}
              wrapperClass={fieldErrors?.softSkills && 'error'}
              subText={fieldErrors?.softSkills}
            />
          </FormGroup>
        </FormContainer>
      </Container>
    </StyledJobExperiences>
  )
})

const StyledJobExperiences = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const FormContainer = styled.div`
  padding: 0.5rem;
`

Skills.displayName = 'Skills'

export default Skills
